import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { APPLY_URL, PAYMENT_URL } from "@/lib/constants";

const HERO_IMG =
  "https://images.pexels.com/photos/31886730/pexels-photo-31886730.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const LINES = [
  ["WE CHARGE $25,000", "TO BUILD"],
  ["AMAZON BUSINESSES.", "THIS YEAR,"],
  ["10 PEOPLE LEARN", "TO BUILD"],
  ["THEIR OWN — FOR", "$6,997."],
];

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col justify-end overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img
          src={HERO_IMG}
          alt=""
          aria-hidden="true"
          className="h-[120%] w-full object-cover opacity-25 saturate-[0.5]"
        />
        <div className="absolute inset-0 bg-[#F4F2EC]/85" />
        <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-[#F4F2EC] to-transparent" />
      </motion.div>

      <div className="mx-auto w-full max-w-[1600px] px-5 pb-10 pt-32 md:px-10">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          data-testid="hero-overline"
          className="mb-6 font-mono2 text-xs uppercase tracking-[0.3em] text-volt"
        >
          The Amazon Launch Program · 10th Anniversary · 10 Seats
        </motion.p>

        <h1
          data-testid="hero-headline"
          className="font-display text-[11vw] font-black leading-[0.92] tracking-tighter md:text-[7vw] lg:text-[5.6vw]"
        >
          {LINES.map((pair, i) => (
            <MaskedLine key={i} delay={0.35 + i * 0.18}>
              {pair[0]}{" "}
              <span className="text-signal">{pair[1]}</span>
            </MaskedLine>
          ))}
        </h1>

        <div className="mt-12 grid gap-10 border-t border-black/10 pt-10 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="max-w-xl font-body text-base text-ink/70 md:text-lg">
              For our 10th year, we're teaching 10 people to do what we do.
              Not a course. Not done-for-you — you own the business, and our
              operators sit beside you from entity setup through your first
              sales.
            </p>
            <div
              data-testid="hero-stats"
              className="mt-8 flex flex-wrap gap-x-8 gap-y-3 font-mono2 text-[11px] uppercase tracking-[0.2em] text-ink/50"
            >
              <span>10 yrs operating</span>
              <span>400+ client stores</span>
              <span>$1.4B managed sales</span>
              <span>5.0 · 143 reviews</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="lg:justify-self-end"
          >
            <div className="flex items-end gap-5">
              <span
                data-testid="hero-anchor-price"
                className="font-display text-2xl font-bold text-ink/40 line-through decoration-signal decoration-2 md:text-3xl"
              >
                $25,000
              </span>
              <span
                data-testid="hero-founder-price"
                className="font-display text-7xl font-black leading-none tracking-tighter text-ink md:text-8xl"
              >
                $6,997
              </span>
            </div>
            <p className="mt-3 font-mono2 text-[11px] uppercase tracking-[0.2em] text-signal">
              One-time · six months · 10 seats
            </p>
            <p
              data-testid="hero-qualification"
              className="mt-6 max-w-md border-l-2 border-signal pl-4 font-body text-xs leading-relaxed text-ink/60"
            >
              Before you apply: this program requires at least{" "}
              <span className="font-semibold text-ink">
                $5,000 in working capital
              </span>{" "}
              for inventory, separate from the program fee. Without deployable
              capital the program cannot be completed.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <a
                href={APPLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-button"
                className="group inline-flex items-center gap-3 bg-signal px-8 py-4 font-mono2 text-sm font-medium uppercase tracking-[0.15em] text-white transition-colors duration-200 hover:bg-ink hover:text-white"
              >
                Apply for 1 of 10 Seats
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </a>
              <a
                href={PAYMENT_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-payment-link"
                className="group inline-flex items-center gap-3 border border-ink/20 px-8 py-4 font-mono2 text-sm font-medium uppercase tracking-[0.15em] text-ink transition-colors duration-200 hover:border-signal hover:text-signal"
              >
                Skip the call — Pay $6,997
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
